import { SporsmalType } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { BesvartSporsmal, CurrentSporsmal, toBesvartSporsmal } from '../reducer/current-steg';
import { UpdateSporsmalSideAction } from '../reducer/sporsmalsflyt-actions';
import { SporsmalSide } from '../reducer/sporsmalsflyt-types';
import {
    copyNextSvarToSporsmalsflytCollection,
    getSporsmalVisibilityStatesFromCollection,
    getVisibleSporsmalFromCollection,
    initializeSporsmalsflytCollection,
    SporsmalsflytCollection,
    updateSporsmalsflytCollection,
} from '../sporsmalsflyt-collection';

export interface CurrentSporsmalSide {
    type: SporsmalType.SporsmalSide;
    sporsmal: SporsmalSide;
    sporsmalsflytCollection: SporsmalsflytCollection;
}

export interface BesvartSporsmalSide {
    type: SporsmalType.SporsmalSide;
    sporsmal: SporsmalSide;
    sporsmalsflytCollection: SporsmalsflytCollection;
    besvarteSporsmal: BesvartSporsmal[];
}

export const getVisibleSporsmal = (currentSporsmalSide: CurrentSporsmalSide): CurrentSporsmal[] =>
    getVisibleSporsmalFromCollection(currentSporsmalSide.sporsmalsflytCollection);

export const getSporsmalVisibilityStates = (
    currentSporsmalSide: CurrentSporsmalSide,
): { sporsmal: CurrentSporsmal; show: boolean }[] =>
    getSporsmalVisibilityStatesFromCollection(currentSporsmalSide.sporsmalsflytCollection);

export const initializeCurrentSporsmalSide = (
    sporsmalSide: SporsmalSide,
    besvarteSporsmal: BesvartSporsmal[],
    kundeinfo?: Kundeinfo,
): CurrentSporsmalSide => {
    return {
        type: SporsmalType.SporsmalSide,
        sporsmal: sporsmalSide,
        sporsmalsflytCollection: initializeSporsmalsflytCollection(
            sporsmalSide.sporsmalsflyt,
            besvarteSporsmal,
            kundeinfo,
        ),
    };
};

export const isCurrentSporsmalSide = (currentSporsmal: CurrentSporsmal): currentSporsmal is CurrentSporsmalSide =>
    currentSporsmal.type === SporsmalType.SporsmalSide;

export const isBesvartSporsmalSide = (besvartSporsmal: CurrentSporsmal): besvartSporsmal is BesvartSporsmalSide =>
    besvartSporsmal.type === SporsmalType.SporsmalSide;

export const toBesvartSporsmalSide = (currentSporsmal: CurrentSporsmal): BesvartSporsmalSide | undefined => {
    if (isCurrentSporsmalSide(currentSporsmal)) {
        const sporsmal: (BesvartSporsmal | undefined)[] = getVisibleSporsmal(currentSporsmal).map(toBesvartSporsmal);
        if (!sporsmal.some((s) => s === undefined)) {
            return {
                ...currentSporsmal,
                besvarteSporsmal: sporsmal,
            };
        }
    }
    return undefined;
};

export const updateCurrentSporsmalSide = (
    currentSporsmal: CurrentSporsmal,
    action: UpdateSporsmalSideAction,
    besvarteSporsmalFromOutsideCollection: BesvartSporsmal[],
): CurrentSporsmal => {
    if (isCurrentSporsmalSide(currentSporsmal)) {
        return {
            ...currentSporsmal,
            sporsmalsflytCollection: updateSporsmalsflytCollection(
                currentSporsmal.sporsmalsflytCollection,
                currentSporsmal.sporsmal.sporsmalsflyt,
                action,
                besvarteSporsmalFromOutsideCollection,
            ),
        };
    }
    return currentSporsmal;
};

export const copyNextSvarToCurrentSporsmalSide = (
    currentSporsmal: CurrentSporsmalSide,
    nextSporsmal: CurrentSporsmalSide,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentSporsmalSide => {
    return {
        ...currentSporsmal,
        sporsmalsflytCollection: copyNextSvarToSporsmalsflytCollection(
            currentSporsmal.sporsmalsflytCollection,
            nextSporsmal.sporsmalsflytCollection,
            currentSporsmal.sporsmal.sporsmalsflyt,
            besvarteSporsmal,
        ),
    };
};
